import { useState } from 'react';

/**
 * Custom hook for managing synchronized calculator inputs
 * Handles homePrice, downPayment, and downPaymentPercent synchronization
 *
 * @param {number} initialHomePrice - Initial home price value
 * @param {number} initialDownPaymentPercent - Initial down payment percentage (default: 20)
 * @returns {Object} State values and handlers
 */
const useCalculatorInputs = (initialHomePrice = 500000, initialDownPaymentPercent = 20) => {
  const [homePrice, setHomePrice] = useState(initialHomePrice);
  const [downPayment, setDownPayment] = useState(
    initialHomePrice * (initialDownPaymentPercent / 100)
  );
  const [downPaymentPercent, setDownPaymentPercent] = useState(initialDownPaymentPercent);

  /**
   * Handle home price changes
   * Updates homePrice and recalculates downPayment based on current percentage
   */
  const handleHomePriceChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setHomePrice(numValue);
    const newDownPayment = numValue * (downPaymentPercent / 100);
    setDownPayment(newDownPayment);
  };

  /**
   * Handle down payment dollar amount changes
   * Updates downPayment and recalculates percentage
   */
  const handleDownPaymentChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setDownPayment(numValue);
    const newPercent = homePrice > 0 ? (numValue / homePrice) * 100 : 0;
    setDownPaymentPercent(Math.min(100, newPercent));
  };

  /**
   * Handle down payment percentage changes
   * Updates percentage and recalculates dollar amount
   */
  const handleDownPaymentPercentChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setDownPaymentPercent(Math.min(100, numValue));
    const newDownPayment = homePrice * (numValue / 100);
    setDownPayment(newDownPayment);
  };

  const loanAmount = homePrice - downPayment;

  return {
    // State values
    homePrice,
    downPayment,
    downPaymentPercent,
    loanAmount,

    // Handlers
    handleHomePriceChange,
    handleDownPaymentChange,
    handleDownPaymentPercentChange,

    // Direct setters (for special cases)
    setHomePrice,
    setDownPayment,
    setDownPaymentPercent
  };
};

export default useCalculatorInputs;
